import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import EnrollmentModal from './EnrollmentModal';

interface Course {
  id: string;
  codigo: string;
  titulo: string;
  descripcion: string;
  duracion: string;
  modalidad: string;
  precio: number;
  imagen_url: string;
  categoria: string;
  nivel: string;
}

const courseImages: Record<string, string> = {
  'ECP1741_2': 'https://public.readdy.ai/ai/img_res/3f2b7f2662a58fba1b30e82622446c58.jpg',
  'ECP1742_2': 'https://readdy.ai/api/search-image?query=Professional%20dog%20behaviorist%20analyzing%20and%20modifying%20problematic%20canine%20behavior%2C%20working%20with%20shepherd%20dog%20in%20controlled%20training%20environment%2C%20simple%20clean%20background%2C%20natural%20lighting%2C%20professional%20educational%20photography&width=800&height=600&seq=course-002&orientation=landscape',
  'ECP1743_2': 'https://readdy.ai/api/search-image?query=Professional%20dog%20groomer%20performing%20hygienic%20care%20on%20beautiful%20dog%20in%20modern%20clean%20grooming%20facility%2C%20professional%20grooming%20tools%20and%20equipment%2C%20simple%20clean%20background%2C%20bright%20natural%20lighting%2C%20high%20quality%20professional%20photography&width=800&height=600&seq=course-003&orientation=landscape',
  'ECP1744_2': 'https://readdy.ai/api/search-image?query=Veterinary%20professional%20demonstrating%20first%20aid%20techniques%20on%20dog%20in%20modern%20veterinary%20clinic%2C%20emergency%20care%20equipment%20and%20medical%20supplies%2C%20simple%20clean%20background%2C%20professional%20medical%20lighting%2C%20educational%20photography&width=800&height=600&seq=course-004&orientation=landscape'
};

export default function CoursesList() {
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedCourseForEnroll, setSelectedCourseForEnroll] = useState<Course | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('cursos')
        .select('*')
        .eq('activo', true)
        .order('orden', { ascending: true });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error al cargar cursos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = (course: Course) => {
    setSelectedCourseForEnroll(course);
    setShowEnrollModal(true);
  };

  const getRDLink = (codigo: string) => {
    switch(codigo) {
      case 'ECP1741_2':
        return 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2023-13800';
      case 'ECP1742_2':
      case 'ECP1743_2':
      case 'ECP1744_2':
        return 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2024-18537';
      default:
        return null;
    }
  };

  const getRDText = (codigo: string) => {
    switch(codigo) {
      case 'ECP1741_2':
        return 'RD 545/2023';
      case 'ECP1742_2':
      case 'ECP1743_2':
      case 'ECP1744_2':
        return 'RD 917/2024';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <p className="mt-4 text-gray-600">Cargando cursos...</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {courses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No hay cursos disponibles en este momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {courses.map((course, index) => (
                <article
                  key={course.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                    <img
                      src={course.imagen_url}
                      alt={course.titulo}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3 md:top-4 md:left-4 bg-teal-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold">
                      {course.codigo}
                    </div>
                    <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 rounded-full flex items-center gap-2">
                      <i className="ri-shield-check-fill text-teal-600 text-sm md:text-base"></i>
                      <span className="text-xs md:text-sm font-semibold text-gray-900">Certificación Oficial</span>
                    </div>
                  </div>
                  
                  <div className="p-5 md:p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
                      {course.titulo}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                      {course.descripcion}
                    </p>

                    <div className="mb-3 md:mb-4 flex items-center gap-3 flex-wrap">
                      {getRDLink(course.codigo) && (
                        <a
                          href={getRDLink(course.codigo)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs md:text-sm font-medium hover:bg-blue-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-text-line"></i>
                          {getRDText(course.codigo)}
                        </a>
                      )}
                    </div>

                    <button
                      onClick={() => setSelectedCourse(selectedCourse === index ? null : index)}
                      className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap mb-4 md:mb-6 text-sm md:text-base"
                    >
                      {selectedCourse === index ? 'Ver menos' : 'Ver más información'}
                      <i className={`ml-2 ${selectedCourse === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                    </button>

                    {selectedCourse === index && (
                      <div className="border-t border-gray-200 pt-4 md:pt-6 space-y-4">
                        <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-4 md:p-6 rounded-xl border border-teal-100">
                          <div className="flex items-start gap-3 mb-3 md:mb-4">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-award-fill text-white text-lg md:text-xl"></i>
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 mb-1 text-sm md:text-base">Certificación Oficial</h4>
                              <p className="text-xs md:text-sm text-gray-700">
                                Este curso está publicado según el <strong>{getRDText(course.codigo)}</strong> del Boletín Oficial del Estado (BOE).
                              </p>
                            </div>
                          </div>
                          
                          {getRDLink(course.codigo) && (
                            <a
                              href={getRDLink(course.codigo)!}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800 transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm"
                            >
                              <i className="ri-external-link-line"></i>
                              Ver documentación oficial en BOE
                            </a>
                          )}
                        </div>

                        <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                          <p className="text-gray-700 text-xs md:text-sm leading-relaxed">
                            Este curso forma parte del programa de formación profesional certificado. 
                            Obtendrás conocimientos prácticos y teóricos que te permitirán desarrollarte 
                            profesionalmente en el sector.
                          </p>
                        </div>

                        <button 
                          onClick={() => handleEnroll(course)}
                          className="w-full rounded-full bg-teal-600 px-5 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-semibold text-white shadow-lg hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer"
                        >
                          {user ? 'Inscribirse Ahora' : 'Iniciar Sesión para Inscribirse'}
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {showEnrollModal && selectedCourseForEnroll && (
        <EnrollmentModal
          isOpen={showEnrollModal}
          onClose={() => {
            setShowEnrollModal(false);
            setSelectedCourseForEnroll(null);
          }}
          course={{
            codigo: selectedCourseForEnroll.codigo,
            titulo: selectedCourseForEnroll.titulo
          }}
        />
      )}
    </>
  );
}
